import { FC, useState, FormEvent } from 'react';
import styled from 'styled-components';
import { apiForgotPassword } from '../ui_components/ApiList';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalDialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 28px auto;
  pointer-events: none;
`;

const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #ffffff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  outline: 0;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const ModalTitle = styled.h5`
  margin: 0;
  line-height: 1.5;
  font-size: 20px;
  font-weight: 500;
  color: #000000;
`;

const CloseButton = styled.button`
  box-sizing: content-box;
  width: 1em;
  height: 1em;
  padding: 4px;
  color: #000000;
  border: 0;
  border-radius: 4px;
  opacity: 0.5;
  background: transparent;
  cursor: pointer;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 1px 0 #fff;

  &:hover {
    opacity: 0.75;
  }

  &:before {
    content: '×';
  }
`;

const ModalBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  padding: 16px;
  color: #000000;
`;

const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid #dee2e6;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  gap: 8px;
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: inline-block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #000000;
`;

const FormInput = styled.input`
  display: block;
  width: 100%;
  padding: 6px 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
  background-color: #ffffff;
  background-image: none;
  border: 1px solid #d3d3d3;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    color: #000000;
    background-color: #ffffff;
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 1;
  }
`;

const Button = styled.button`
  display: inline-block;
  font-weight: 400;
  line-height: 1.5;
  color: #000000;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  border: 1px solid transparent;
  padding: 6px 12px;
  font-size: 16px;
  border-radius: 4px;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ButtonPrimary = styled(Button)`
  color: #ffffff;
  background-color: #0d6efd;
  border-color: #0d6efd;

  &:hover:not(:disabled) {
    color: #ffffff;
    background-color: #0b5ed7;
    border-color: #0a58ca;
  }

  &:focus {
    color: #ffffff;
    background-color: #0b5ed7;
    border-color: #0a58ca;
    box-shadow: 0 0 0 4px rgba(49, 132, 253, 0.5);
  }
`;

const ButtonSecondary = styled(Button)`
  color: #ffffff;
  background-color: #6c757d;
  border-color: #6c757d;

  &:hover:not(:disabled) {
    color: #ffffff;
    background-color: #5c636a;
    border-color: #565e64;
  }

  &:focus {
    color: #ffffff;
    background-color: #5c636a;
    border-color: #565e64;
    box-shadow: 0 0 0 4px rgba(130, 138, 145, 0.5);
  }
`;

const ErrorText = styled.p`
  margin-top: 4px;
  margin-bottom: 0;
  color: #dc3545;
  font-size: 14px;
`;

const StatusMessage = styled.div`
  text-align: right;
  padding: 0 16px;
  margin-top: 8px;
  color: #000000;
`;

const SuccessText = styled.div`
  text-align: center;
  padding: 12px 16px;
  color: #198754;
  font-weight: 500;
  margin-top: 8px;
`;

const InfoText = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  line-height: 1.5;
  color: #000000;
`;

interface ForgotPasswordModaleProps {
  closeModalClick: () => void;
  openLogin: () => void;
}

const ForgotPasswordModale: FC<ForgotPasswordModaleProps> = ({
  closeModalClick,
  openLogin,
}) => {
  const [errorsForm, setErrorsForm] = useState<Record<string, string[]>>({});
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsLoadingForm(true);
    setErrorsForm({});

    const formData = new FormData(event.currentTarget);
    const email = formData.get('email') as string;

    try {
      const response = await apiForgotPassword({ email });

      if (response.errors) {
        setErrorsForm(response.errors);
      } else {
        setIsSuccess(true);
        setSuccessMessage(response.message);
      }
    } catch {
      setErrorsForm({ email: ['Errore durante la richiesta. Riprova.'] });
    } finally {
      setIsLoadingForm(false);
    }
  };

  return (
    <Container>
      <ModalDialog>
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader>
              <ModalTitle>Password Dimenticata</ModalTitle>
              <CloseButton type="button" aria-label="Close" onClick={closeModalClick} />
            </ModalHeader>
            <ModalBody>
              <InfoText>
                Inserisci l'email associata al tuo account. Se esiste, riceverai un link
                per reimpostare la password.
              </InfoText>

              {!isSuccess ? (
                <FormGroup>
                  <FormLabel htmlFor="forgot-email">Email:</FormLabel>
                  <FormInput
                    type="email"
                    id="forgot-email"
                    name="email"
                    disabled={isLoadingForm}
                  />
                  {errorsForm.email && <ErrorText>{errorsForm.email[0]}</ErrorText>}
                </FormGroup>
              ) : (
                <SuccessText>{successMessage}</SuccessText>
              )}
            </ModalBody>
            <ModalFooter>
              <FooterLeft>
                <ButtonSecondary type="button" onClick={openLogin}>
                  Torna al Login
                </ButtonSecondary>
              </FooterLeft>
              <FooterRight>
                {!isSuccess && (
                  <ButtonPrimary type="submit" disabled={isLoadingForm}>
                    Invia
                  </ButtonPrimary>
                )}
              </FooterRight>
            </ModalFooter>
            {isLoadingForm && <StatusMessage>Caricamento..</StatusMessage>}
          </form>
        </ModalContent>
      </ModalDialog>
    </Container>
  );
};

export default ForgotPasswordModale;
